import { forwardRef, useEffect, useImperativeHandle, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from 'react-query';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { yupResolver } from '@hookform/resolvers/yup';

import {
  downloadFile,
  getReviseLogs,
  requestUpdate,
  updateUpdateRequest,
} from 'apis/projectProposals.api';
import { createRequestUpdateValidationSchema } from 'features/ReviseProjectProposal/validation';
import { FileInput, TextareaInput } from 'components/FormControl';
import Loading from 'components/Loading';
import { TOAST } from 'constant';
import { toast } from 'layout';
import { formatFileName, mapError } from 'utils/func';

const formOptions = { resolver: yupResolver(createRequestUpdateValidationSchema) };

const RequestUpdateForm = forwardRef((props, ref) => {
  // #region Data
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);
  const [requestControl, setRequestControl] = useState(null);

  const {
    handleSubmit,
    control,
    trigger,
    reset,
    setValue,
    watch,
    formState: { errors, dirtyFields },
  } = useForm({ ...formOptions, mode: 'onChange' });

  const { mutate: mutateDownloadFile } = useMutation(downloadFile);
  const { mutate: mutateRequestUpdate, isLoading: isRequestUpdateLoading } =
    useMutation(requestUpdate);
  const { mutate: mutateUpdateUpdateRequest, isLoading: isUpdateUpdateRequestLoading } =
    useMutation(updateUpdateRequest);

  const {
    data: updateRequestsData,
    isLoading: isUpdateRequestsLoading,
    refetch: refetchUpdateRequests,
  } = useQuery(
    ['projectProposalReviseLogs', requestControl?.value?.reference, 'REQUEST_UPDATE'],
    () =>
      getReviseLogs({
        projectProposalId: requestControl?.value?.reference,
        types: ['REQUEST_UPDATE'],
        isOpened: true,
      }),
    {
      enabled: !!requestControl?.value?.reference,
      staleTime: 0,
    }
  );

  const updateRequest = useMemo(() => {
    if (updateRequestsData?.data?.length > 0) {
      return updateRequestsData?.data[0];
    }
    return null;
  }, [updateRequestsData]);

  const oldCommentFileUrls = watch('oldCommentFileUrls');

  const { mode, mutate } = useMemo(() => {
    const currMode = updateRequestsData?.data?.length > 0 ? 'updateUpdateRequest' : 'requestUpdate';

    const mutates = {
      requestUpdate: mutateRequestUpdate,
      updateUpdateRequest: mutateUpdateUpdateRequest,
    };
    const currMutate = mutates[currMode];

    return { mode: currMode, mutate: currMutate };
  }, [requestControl, updateRequestsData]);

  // #endregion

  // #region Event
  useImperativeHandle(
    ref,
    () => ({
      open: (_requestControl) => {
        setRequestControl(_requestControl);
        setVisible(true);
        refetchUpdateRequests();
      },
    }),
    []
  );

  useEffect(() => {
    if (updateRequest) {
      setValue('comment', updateRequest?.comment ?? null);
      if (updateRequest?.commentFileUrls?.length > 0) {
        setValue('oldCommentFileUrls', updateRequest?.commentFileUrls ?? []);
      }
    }
  }, [updateRequest]);

  const handleDownloadFile = (path, projectProposalId) => {
    mutateDownloadFile(
      {
        projectProposalId,
        path,
      },
      {
        onSuccess: async (res) => {
          if (res?.data) {
            const file = new Blob([res.data]);

            const link = document.createElement('a');
            link.href = URL.createObjectURL(file);
            link.download = formatFileName(path);

            link.click();
          }
        },
      }
    );
  };

  const onSubmit = (data) => {
    const formData = new FormData();

    if (mode === 'updateUpdateRequest') {
      formData.append('shouldResendRequest', 'true');
      formData.append('isOpened', 'true');
    }

    if (data?.commentFiles?.length > 0) {
      data?.commentFiles?.forEach((file) => {
        formData.append('commentFiles', file);
      });
    }
    if (data?.comment) {
      formData.append('comment', data?.comment);
    }

    if (updateRequest?.id) {
      formData.append('id', updateRequest?.id);
    }
    if (requestControl?.value?.reference) {
      formData.append('projectProposalId', requestControl?.value?.reference);
    }
    formData.append('oldCommentFileUrls', JSON.stringify(oldCommentFileUrls ?? []));

    mutate(formData, {
      onSuccess: () => {
        toast(TOAST.SUCCESS, t(`projectProposal.${mode}.success`));
        reset();
        refetchUpdateRequests();
        setVisible(false);
      },
      onError: (error) => {
        toast(TOAST.ERROR, mapError(t, error));
      },
    });
  };

  const checkFormValid = async () => {
    const isValid = await trigger();
    if (!isValid) toast(TOAST.ERROR, t('errorMessage.validationErrorMessage'));
  };
  // #endregion

  return (
    <Dialog
      header={
        // eslint-disable-next-line react/jsx-wrap-multilines
        <div className="flex justify-content-between">Yêu cầu giải trình chỉnh sửa</div>
      }
      visible={visible}
      style={{ width: '700px' }}
      onHide={() => {
        reset();
        refetchUpdateRequests();
        setVisible(false);
      }}
      closable={!isRequestUpdateLoading && !isUpdateUpdateRequestLoading}
    >
      <div className="relative">
        {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */}
        <form
          onSubmit={handleSubmit(onSubmit)}
          className="p-fluid flex flex-column"
          autoComplete="off"
        >
          <div className="grid">
            <div className="col-12 mb-2">
              <TextareaInput
                label={t('projectProposal.requestUpdate.comment')}
                name="comment"
                control={control}
                errors={errors}
                row={5}
                isRequired
                disabled={isRequestUpdateLoading || isUpdateUpdateRequestLoading}
              />
            </div>

            <div className="col-12 mb-2">
              <label htmlFor="commentFiles">
                {t('projectProposal.requestUpdate.commentFile')}{' '}
              </label>
              {oldCommentFileUrls?.map((fileUrl, index) => (
                <div key={fileUrl?.id ?? index} className="flex justify-content-between m-1 p-1">
                  <span
                    className="text-blue-500 underline font-italic text-base"
                    onClick={() =>
                      handleDownloadFile(fileUrl?.name, requestControl?.value?.reference)
                    }
                  >
                    {formatFileName(fileUrl?.name)}
                  </span>
                  <i
                    className="pi pi-times text-red-500 text-xl ml-2"
                    onClick={() => {
                      const fileUrls = [...oldCommentFileUrls];
                      fileUrls.splice(index, 1);
                      setValue('oldCommentFileUrls', fileUrls, { shouldDirty: true });
                    }}
                  />
                </div>
              ))}
              <FileInput
                name="commentFiles"
                control={control}
                errors={errors}
                accept=".pdf,.docx,.doc,.xlsx,.xls,.csv"
                acceptOnly="pdfWordExcelOnly"
                multiple
                disabled={isRequestUpdateLoading || isUpdateUpdateRequestLoading}
              />
            </div>
          </div>

          <div className="grid mt-2">
            <div className="col-6">
              <Button
                label={t('formLayout.action.cancel')}
                type="button"
                severity="danger"
                onClick={() => {
                  reset();
                  refetchUpdateRequests();
                  setVisible(false);
                }}
                disabled={isRequestUpdateLoading || isUpdateUpdateRequestLoading}
              />
            </div>
            <div className="col-6">
              <Button
                label={
                  mode === 'updateUpdateRequest'
                    ? t('projectProposal.updateExplanationRequest.updateAndResend')
                    : t('projectProposal.requestExplanation.sendRequest')
                }
                className="bg-cyan-400 border-cyan-400 hover:bg-cyan-500"
                type="submit"
                onClick={checkFormValid}
                disabled={
                  isRequestUpdateLoading ||
                  isUpdateUpdateRequestLoading ||
                  !(Object.keys(dirtyFields)?.length > 0)
                }
              />
            </div>
          </div>
        </form>
      </div>

      {(isRequestUpdateLoading || isUpdateUpdateRequestLoading || isUpdateRequestsLoading) && (
        <Loading />
      )}
    </Dialog>
  );
});

export default RequestUpdateForm;

RequestUpdateForm.propTypes = {};
